import { ArrowUpDown, ChevronRight, MoreHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Badge } from './ui/badge';
import { useGetMyInvestigationsQuery } from '@/store/api/apiSlice';
import { Investigation, InvestigationStatus } from '@/types';

const OngoingInvestigationTable = () => {
  const navigate = useNavigate();
  const { data: allInvestigations = [], isLoading } = useGetMyInvestigationsQuery();

  // Filter for ongoing investigations (new, pending, awaiting_approval, in_progress)
  const investigations = allInvestigations.filter(inv =>
    inv.status === InvestigationStatus.NEW ||
    inv.status === InvestigationStatus.PENDING ||
    inv.status === InvestigationStatus.AWAITING_APPROVAL ||
    inv.status === InvestigationStatus.IN_PROGRESS
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getEmployeeName = (inv: Investigation) => {
    return `${inv.employeeFirstName}${inv.employeeMiddleName ? ' ' + inv.employeeMiddleName : ''} ${inv.employeeLastName}`;
  };

  const getCheckTypeBadgeClass = (checkType: string) => {
    return checkType === 'standard'
      ? 'bg-indigo-50 text-indigo-600 border-indigo-100'
      : 'bg-orange-50 text-[#FF6B35] border-orange-100';
  };

  return (
    <div className="bg-white rounded-2xl md:rounded-3xl shadow-sm overflow-hidden p-4 md:p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Ongoing Investigation</h2>
      </div>

      {/* Mobile Card List */}
      <div className="md:hidden flex-1 overflow-y-auto space-y-3">
        {isLoading ? (
          <p className="text-center text-gray-500 py-8">Loading...</p>
        ) : investigations.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No ongoing investigations</p>
        ) : (
          investigations.map((investigation) => (
            <button
              type="button"
              key={investigation.id}
              onClick={() => navigate(`/investigations/${investigation.id}`)}
              className="w-full text-left border border-gray-100 rounded-xl p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900">{getEmployeeName(investigation)}</span>
                <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
              </div>
              <div className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className={`${getCheckTypeBadgeClass(investigation.checkType)} px-2 py-0.5 rounded-lg font-medium text-xs capitalize`}
                >
                  {investigation.checkType}
                </Badge>
                <span className="text-xs text-gray-500">{formatDate(investigation.createdAt)}</span>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto overflow-y-auto -mx-6 flex-1">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="px-6 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  S/N
                  <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                </div>
              </th>
              <th className="px-6 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  Employee Name
                  <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                  <MoreHorizontal className="w-3.5 h-3.5 text-[#FF6B35]" />
                </div>
              </th>
              <th className="px-6 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  Check Type
                  <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                  <MoreHorizontal className="w-3.5 h-3.5 text-[#FF6B35]" />
                </div>
              </th>
              <th className="px-6 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  Date Created
                  <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                  <MoreHorizontal className="w-3.5 h-3.5 text-[#FF6B35]" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : investigations.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  No ongoing investigations
                </td>
              </tr>
            ) : (
              investigations.map((investigation, index) => (
                <tr
                  key={investigation.id}
                  onClick={() => navigate(`/investigations/${investigation.id}`)}
                  className={`border-b border-gray-50 last:border-b-0 cursor-pointer hover:bg-gray-100 transition-colors ${
                    index % 2 === 0 ? 'bg-gray-50/30' : 'bg-white'
                  }`}
                >
                  <td className="px-6 py-4 text-start">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300 text-gray-400 focus:ring-gray-400"
                      />
                      <span className="text-sm text-gray-700 text-start">{index + 1}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-start">
                    <span className="text-sm text-gray-900 text-start">{getEmployeeName(investigation)}</span>
                  </td>
                  <td className="px-6 py-4 text-start">
                    <Badge
                      variant="outline"
                      className={`${getCheckTypeBadgeClass(investigation.checkType)} px-4 py-1 rounded-lg font-medium text-sm capitalize`}
                    >
                      {investigation.checkType}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-start">
                    <span className="text-sm text-gray-700 text-start">{formatDate(investigation.createdAt)}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OngoingInvestigationTable;
